import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { SkillImportDialogComponent } from '../../components/dialogs/skill-import-dialog/skill-import-dialog.component';
import { SkillLibraryDialogComponent } from '../../components/dialogs/skill-library-dialog/skill-library-dialog.component';
import {
  AlTableBadgeComponent,
  AlTableBadgeVariant,
} from '../../components/features/table-badge/table-badge.component';
import { AlTableTextComponent } from '../../components/features/table-text/table-text.component';
import { AlTableColumn, AlTableRowAction } from '../../components/features/table/table.component';
import { Skill, SkillService } from '../../services/skill.service';

interface SkillRow extends Skill {
  agentsCount: number;
  filesCount: number;
}

/**
 * KI-Agenten › Skills — the client's library of procedures (admin/manager).
 * A skill is written here or imported (Claude Skills format); agents get
 * only its name and description until a task needs the whole procedure.
 */
@Component({
  selector: 'app-skills',
  templateUrl: './skills.component.html',
  styleUrl: './skills.component.scss',
})
export class SkillsComponent implements OnInit {
  rows: SkillRow[] = [];
  isLoading = false;
  columns: AlTableColumn[] = [];
  rowActions: AlTableRowAction[] = [];
  globalActions: { key: string; title: string; icon: string }[] = [];

  constructor(
    private skills: SkillService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private router: Router,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.buildTable();
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.skills.list().subscribe({
      next: (data) => {
        // Built once per load (CLAUDE.md rule 18).
        this.rows = data.map((s) => ({ ...s, agentsCount: s.assigned_bot_ids.length, filesCount: s.files.length }));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastr.danger(this.t.instant('pages.skills.errors.loadFailed'), '');
      },
    });
  }

  private statusBadge(s: Skill): { label: string; variant: AlTableBadgeVariant; tooltip: string } {
    if (s.is_active) {
      return { label: this.t.instant('pages.skills.status.active'), variant: 'success', tooltip: this.t.instant('pages.skills.tooltips.statusActive') };
    }
    if (s.source === 'import' && s.version === 1) {
      return { label: this.t.instant('pages.skills.status.review'), variant: 'warning', tooltip: this.t.instant('pages.skills.tooltips.statusReview') };
    }
    return { label: this.t.instant('pages.skills.status.inactive'), variant: 'muted', tooltip: this.t.instant('pages.skills.tooltips.statusInactive') };
  }

  private buildTable(): void {
    this.columns = [
      {
        key: 'name',
        kind: 'name',
        title: this.t.instant('pages.skills.fields.name'),
        width: '22%',
        sortable: true,
        component: AlTableTextComponent,
        componentInputs: ({ row }) => ({ text: row.title || row.name, max: 80, suffix: row.title && row.title !== row.name ? row.name : undefined }),
      },
      {
        key: 'description',
        kind: 'text',
        title: this.t.instant('pages.skills.fields.description'),
        width: '32%',
        component: AlTableTextComponent,
        componentInputs: ({ value }) => ({ text: value, max: 160 }),
      },
      {
        key: 'is_active',
        title: this.t.instant('words.status'),
        width: '12%',
        component: AlTableBadgeComponent,
        componentInputs: ({ row }) => this.statusBadge(row),
      },
      { key: 'agentsCount', kind: 'number', title: this.t.instant('pages.skills.fields.agents'), width: '8%', sortable: true },
      { key: 'filesCount', kind: 'number', title: this.t.instant('pages.skills.fields.files'), width: '8%', sortable: true },
      { key: 'load_count', kind: 'number', title: this.t.instant('pages.skills.fields.loads'), width: '8%', sortable: true },
    ];
    this.rowActions = [
      { key: 'open', title: this.t.instant('pages.skills.open'), icon: 'edit-outline' },
      { key: 'delete', title: this.t.instant('buttons.delete'), icon: 'trash-outline' },
    ] as AlTableRowAction[];
    this.globalActions = [
      { key: 'create', title: this.t.instant('pages.skills.add'), icon: 'plus-outline' },
      { key: 'library', title: this.t.instant('pages.skills.library.open'), icon: 'book-open-outline' },
      { key: 'import', title: this.t.instant('pages.skills.import'), icon: 'upload-outline' },
    ];
  }

  onTableEvent(payload: { event: string; data: any }): void {
    if (payload.event === 'globalAction') {
      const key = payload.data?.action?.key;
      if (key === 'import') this.openImport();
      else if (key === 'library') this.openLibrary();
      else this.router.navigate(['/skills', 'new']);
      return;
    }
    if (payload.event !== 'rowAction') return;
    const row = payload.data?.row as SkillRow;
    if (!row) return;
    if (payload.data?.action?.key === 'open') this.router.navigate(['/skills', row.id]);
    if (payload.data?.action?.key === 'delete') this.confirmDelete(row);
  }

  openImport(): void {
    this.dialogService.open(SkillImportDialogComponent, {
      context: {
        onImported: (skill: Skill) => this.afterImport(skill),
        onImportedMany: (skills: Skill[]) => {
          this.toastr.success(this.t.instant('pages.skills.importedMany', { count: skills.length }), '');
          this.load();
        },
      },
    });
  }

  openLibrary(): void {
    this.dialogService.open(SkillLibraryDialogComponent, {
      context: { onImported: (skill: Skill) => this.afterImport(skill) },
    });
  }

  private afterImport(skill: Skill): void {
    this.toastr.success(this.t.instant('pages.skills.imported'), '');
    this.router.navigate(['/skills', skill.id]);
  }

  private confirmDelete(row: Skill): void {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.skills.deleteTitle',
        message: this.t.instant('pages.skills.deleteMessage', { name: row.title || row.name, agents: row.assigned_bot_ids.length }),
        onConfirm: () => {
          ref.close();
          this.skills.delete(row.id).subscribe({
            next: () => {
              this.toastr.success(this.t.instant('pages.skills.deleted'), '');
              this.load();
            },
            error: () => this.toastr.danger(this.t.instant('pages.skills.errors.saveFailed'), ''),
          });
        },
      },
    });
  }
}
